import React, { useState, useEffect } from 'react';
import { Container, Typography, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Button, Box, Select, MenuItem, FormControl, InputLabel, Modal, TextField } from '@mui/material';
import axios from 'axios';

const CoachingAdminUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [newUserFormData, setNewUserFormData] = useState({
    name: '',
    email: '',
    password: '',
    role_id: 3, // Default to Student role
    customer_id: null, // Will be set from the logged-in admin's customer_id
  });

  // Get customer_id from local storage or context
  const coachingAdminCustomerId = localStorage.getItem('customer_id'); // Assuming customer_id is stored here

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    setNewUserFormData(prevData => ({ ...prevData, customer_id: coachingAdminCustomerId }));
  }, [coachingAdminCustomerId]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };
      if (coachingAdminCustomerId) {
        const res = await axios.get(`/api/customers/${coachingAdminCustomerId}/users`, config);
        setUsers(res.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };
      // Assuming an endpoint to get all roles
      const res = await axios.get('/api/roles', config);
      setRoles(res.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const handleRoleChange = async (userId, newRoleId) => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };
      await axios.put(`/api/users/${userId}/role`, { role_id: newRoleId }, config);
      fetchUsers(); // Refresh the user list
    } catch (error) {
      console.error('Error changing user role:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const token = localStorage.getItem('token');
        const config = {
          headers: { Authorization: `Bearer ${token}` },
        };
        await axios.delete(`/api/users/${userId}`, config);
        fetchUsers(); // Refresh the user list
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleNewUserFormChange = (e) => {
    setNewUserFormData({ ...newUserFormData, [e.target.name]: e.target.value });
  };

  const handleAddUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };
      await axios.post('/api/users', newUserFormData, config); // Assuming a general user creation endpoint
      fetchUsers(); // Refresh the user list
      handleCloseModal();
      setNewUserFormData({
        name: '',
        email: '',
        password: '',
        role_id: 3,
        customer_id: coachingAdminCustomerId,
      });
    } catch (error) {
      console.error('Error adding user:', error);
      // Handle error (e.g., display error message)
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Manage Coaching Users
      </Typography>
      <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button variant="contained" onClick={handleOpenModal}>Add New User</Button>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <FormControl size="small">
                      <Select
                        value={user.role_id || ''}
                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          Select Role
                        </MenuItem>
                        {roles.map((role) => (
                          <MenuItem key={role.id} value={role.id}>
                            {role.role_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => handleDeleteUser(user.id)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="add-user-modal-title"
        aria-describedby="add-user-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}>
          <Typography id="add-user-modal-title" variant="h6" component="h2" gutterBottom>
            Add New User
          </Typography>
          <TextField
            fullWidth
            margin="normal"
            label="Name"
            name="name"
            value={newUserFormData.name}
            onChange={handleNewUserFormChange}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Email"
            name="email"
            type="email"
            value={newUserFormData.email}
            onChange={handleNewUserFormChange}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Password"
            name="password"
            type="password"
            value={newUserFormData.password}
            onChange={handleNewUserFormChange}
          />
           <FormControl fullWidth margin="normal">
              <InputLabel id="new-user-role-label">Role</InputLabel>
              <Select
                labelId="new-user-role-label"
                id="new-user-role"
                name="role_id"
                value={newUserFormData.role_id}
                label="Role"
                onChange={handleNewUserFormChange}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.role_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button onClick={handleCloseModal} sx={{ mr: 2 }}>Cancel</Button>
            <Button variant="contained" onClick={handleAddUser}>Add User</Button>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

export default CoachingAdminUserManagement;